<script setup>
import { computed } from 'vue'
import IconPlayMarket from "@js/components/Icons/IconPlayMarket.vue";
import IconAppStore from "@js/components/Icons/IconAppStore.vue";
import IconLink from "@js/components/Icons/IconLink.vue";

const props = defineProps({
    image: {
        type: String,
        default: '',
    },
    title: {
        type: String,
        default: '',
    },
    subtitle: {
        type: String,
        default: '',
    },
    category: {
        type: String,
        default: '',
    },
    appStoreLink: {
        type: String,
        default: '',
    },
    googlePlayLink: {
        type: String,
        default: '',
    },
    logoBgColor: {
        type: String,
        default: 'bg-white',
    },
    link: {
        type: String,
        default: '',
    },
    linkTarget: {
        type: String,
        default: '_blank',
    },
})

const hasStores = computed(() => {
    return !!(props.appStoreLink || props.googlePlayLink)
})

const hasLink = computed(() => {
    return !!props.link
})
</script>

<template>
    <div
        class="group flex h-full w-full flex-col rounded-3xl border border-neutral-200 bg-neutral-100 p-5 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-xl sm:p-6 md:p-7"
    >
        <div class="flex items-start gap-4">
            <div
                v-if="image"
                class="shrink-0 overflow-hidden rounded-2xl shadow-lg"
                :class="[logoBgColor]"
            >
                <component
                    :is="hasLink ? 'a' : 'div'"
                    :href="hasLink ? link : null"
                    :target="hasLink ? linkTarget : null"
                    :rel="hasLink ? 'noopener noreferrer' : null"
                    class="block"
                >
                    <img
                        :src="image"
                        :alt="title || 'Preview image'"
                        class="h-20 w-20 object-contain p-3 transition duration-300 group-hover:scale-[1.02] sm:h-24 sm:w-24"
                    >
                </component>
            </div>

            <div class="flex min-w-0 flex-1 flex-col self-stretch">
                <div
                    v-if="title"
                    class="break-words text-base font-semibold leading-tight sm:text-lg"
                >
                    <component
                        :is="hasLink ? 'a' : 'span'"
                        :href="hasLink ? link : null"
                        :target="hasLink ? linkTarget : null"
                        :rel="hasLink ? 'noopener noreferrer' : null"
                        class="inline-flex items-center gap-1 text-sky-600 transition hover:text-sky-700"
                    >
                        <span>{{ title }}</span>
                        <IconLink v-if="hasLink" class="opacity-60 w-4 h-4"/>
                    </component>
                </div>

                <div
                    v-if="subtitle"
                    class="mt-1 text-sm leading-tight text-neutral-800"
                >
                    {{ subtitle }}
                </div>

                <div
                    v-if="category"
                    class="mt-auto pt-3"
                >
                    <div
                        class="inline-flex rounded-full bg-white px-3 py-1 text-xs font-medium text-neutral-500 ring-1 ring-neutral-200 shadow"
                    >
                        {{ category }}
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="$slots.stats"
            class="mt-6 grid grid-cols-1 gap-3"
        >
            <slot name="stats" />
        </div>

        <div
            v-if="$slots.default"
            class="mt-6"
        >
            <div class="text-base font-semibold text-black sm:text-lg">
                Реализовано:
            </div>

            <div class="mt-1 text-sm font-medium leading-relaxed text-neutral-800">
                <slot />
            </div>
        </div>

        <div
            v-if="hasStores"
            class="mt-auto pt-6"
        >
            <div class="grid h-[50px] grid-cols-2 items-center gap-3">
                <a
                    v-if="appStoreLink"
                    :href="appStoreLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="h-full"
                >
                    <IconAppStore class="h-full w-auto" />
                </a>

                <a
                    v-if="googlePlayLink"
                    :href="googlePlayLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="h-full"
                >
                    <IconPlayMarket class="h-full w-auto" />
                </a>
            </div>
        </div>
    </div>
</template>
